import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTable } from "@/lib/store";
import { Card, Kicker, Chip } from "@/components/kit";
import { ArrowUpRight, Check } from "lucide-react";

const TONE = {
  now: { color: "var(--rust)", label: "Now" },
  soon: { color: "var(--amber)", label: "This month" },
  later: { color: "var(--ink-mute)", label: "Later" },
};

export const useActions = () => {
  const { yearId } = useTable();
  const [state, setState] = useState({ items: [], counts: { now: 0, yours: 0 } });

  const load = useCallback(async () => {
    if (!yearId) return;
    try {
      const { data } = await api.get("/actions", { params: { yearId } });
      setState(data);
    } catch {
      setState({ items: [], counts: { now: 0, yours: 0 } });
    }
  }, [yearId]);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
};

export const ActionList = ({ items, limit }) => {
  const shown = limit ? items.slice(0, limit) : items;

  if (items.length === 0) {
    return (
      <Card className="p-6 flex items-center gap-4" data-testid="actions-clear">
        <span className="flex items-center justify-center rounded-full shrink-0"
          style={{ width: 34, height: 34, background: "var(--green-wash)", color: "var(--green)" }}>
          <Check size={16} />
        </span>
        <div>
          <p className="serif text-lg leading-snug">Nothing is waiting on you.</p>
          <p className="text-[13px] mt-0.5" style={{ color: "var(--ink-mute)" }}>
            The table is in good order.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="card-t !p-0 overflow-hidden divide-hair" data-testid="action-list">
      {shown.map((a) => {
        const tone = TONE[a.severity] || TONE.later;
        return (
          <Link
            key={a.key}
            to={a.to}
            data-testid={`action-${a.key}`}
            className="flex items-center gap-4 px-5 py-4 hover:bg-[var(--paper-deep)] transition-colors"
          >
            <span className="rounded-full shrink-0" style={{ width: 7, height: 7, background: tone.color }} />
            <div className="min-w-0 flex-1">
              <div className="text-[15px] leading-snug">{a.title}</div>
              <p className="text-[13px] mt-0.5 truncate" style={{ color: "var(--ink-mute)" }}>
                {a.detail}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              {a.scope === "forum" && <Chip>Forum</Chip>}
              <Chip color={tone.color}>{tone.label}</Chip>
            </div>
            <ArrowUpRight size={14} className="shrink-0" style={{ color: "var(--ink-mute)" }} />
          </Link>
        );
      })}
    </div>
  );
};

export const WhatNeedsYou = ({ limit = 6 }) => {
  const { items, counts } = useActions();
  return (
    <section data-testid="what-needs-you">
      <div className="flex items-end justify-between gap-4 mb-3">
        <Kicker>
          What needs you{counts.now ? ` · ${counts.now} now` : ""}
        </Kicker>
        {items.length > limit && (
          <Link to="/actions" className="kicker !text-[9px] hover:opacity-70 transition-opacity"
            data-testid="all-actions-link">
            All {items.length} →
          </Link>
        )}
      </div>
      <ActionList items={items} limit={limit} />
    </section>
  );
};
